import { ItemWithEditable, Metric, ProductPPG, PromoConfigDictionary, PromoEvent } from 'types/tradePromoOptimization';
import { sendNotification } from 'utils/helperMethods';
import { Parser } from 'expr-eval';
import { PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import dayjs from 'dayjs';
import _ from 'lodash';
import { TpoMessages } from 'constants/tpo';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const parser = new Parser();

const countCharacters = (str: string, char: string): number => str.split(char).length - 1;

export const handleMetricInputChange = (
    promoConfigDictionary: PromoConfigDictionary,
    selectedEventInfo: PromoEvent | null,
    metricId: number,
    productName: string,
    value: string,
    isMetric: boolean,
) => {
    if (!selectedEventInfo?.currentPromoTactic) return;
    const stringValue = String(value).trim();

    const { allowNegative, allowedDecimals, name, name_key } = isMetric
        ? promoConfigDictionary.promoMetrics[metricId]
        : promoConfigDictionary.spendMetrics[metricId];

    // Check for invalid characters (anything other than digits, '-', or '.')
    const invalidChars = stringValue.split('').filter((char) => !char.match(/[\d.-]/));
    if (invalidChars.length > 0) {
        sendNotification('error', `Invalid characters entered: ${invalidChars.join(', ')}`);
        return;
    }

    // Ensure '-' appears only at the beginning and is allowed
    const dashCount = countCharacters(stringValue, '-');
    if (dashCount > 1 || (dashCount === 1 && stringValue[0] !== '-')) {
        sendNotification('error', `${name} contains an invalid negative sign.`);
        return;
    }

    if (!allowNegative && stringValue.startsWith('-')) {
        sendNotification('error', `${name} can't be negative.`);
        return;
    }

    // Ensure only one decimal point
    const dotCount = countCharacters(stringValue, '.');
    if (dotCount > 1) {
        sendNotification('error', `${name} contains multiple decimal points.`);
        return;
    }

    // Validate decimal places
    if (allowedDecimals > 0) {
        const parts = stringValue.split('.');
        if (parts.length === 2 && parts[1].length > allowedDecimals) {
            sendNotification('error', `${name} can have up to ${allowedDecimals} decimal places only.`);
            return;
        }
    } else if (stringValue.includes('.')) {
        sendNotification('error', `${name} must be a whole number and cannot contain decimals.`);
        return;
    }

    const metricsKey = isMetric ? 'metrics' : 'spendDetails';

    // Update the selected event's promo tactic
    let updatedPPGs = selectedEventInfo.currentPromoTactic.ppgs.map((ppg: ProductPPG) => {
        if (ppg.name === productName) {
            return {
                ...ppg,
                [metricsKey]: ppg[metricsKey].map((metric) => {
                    if (metric.id === metricId) {
                        return { ...metric, value: stringValue }; // Keep as string for UI
                    }
                    return metric;
                }),
            };
        }
        return ppg;
    });

    // Update the selected event's promo tactic
    updatedPPGs = updatedPPGs.map((ppg: ProductPPG) => {
        // Compute affected KPIs dynamically (pass original string value)
        if (ppg.name === productName) {
            return computeCascadingKPIs(promoConfigDictionary, name_key, stringValue, ppg);
        }
        return ppg;
    });

    return updatedPPGs;
};

const computeCascadingKPIs = (
    promoConfigDictionary: PromoConfigDictionary,
    updatedKpi: any,
    newValue: any,
    ppg: ProductPPG,
) => {
    const allMetricKpiValues: any = {};

    // Extract current KPI values from `ppgs`
    ppg.metrics.forEach((metric: Metric) => {
        allMetricKpiValues[promoConfigDictionary.promoMetrics[metric.id].name_key] = metric.value;
    });
    ppg.spendDetails.forEach((metric: Metric) => {
        allMetricKpiValues[promoConfigDictionary.spendMetrics[metric.id].name_key] = metric.value;
    });

    allMetricKpiValues[updatedKpi] = newValue;

    // Recursive function to update all dependencies
    const updateDependencies = (kpi: string) => {
        if (promoConfigDictionary.kpiDependencies[kpi]) {
            promoConfigDictionary.kpiDependencies[kpi].forEach((dependentKpi: any) => {
                const formula = promoConfigDictionary.kpiFormulas[dependentKpi];
                if (formula) {
                    try {
                        // Ensure only valid numbers are used in the formula
                        const expression = formula.replace(/\b[A-Za-z_]+\b/g, (match: any) => {
                            const value = allMetricKpiValues[match];
                            return value !== undefined && !isNaN(Number(value)) ? value.toString() : '0'; // Avoid NaN issues
                        });

                        let evaluatedValue = parser.evaluate(expression);

                        // Check for NaN or Infinity
                        if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
                            evaluatedValue = 0;
                        }

                        allMetricKpiValues[dependentKpi] = evaluatedValue;
                        updateDependencies(dependentKpi); // Recurse to handle cascading dependencies
                    } catch (error) {
                        console.error(`Error evaluating formula for ${dependentKpi}:`, error);
                    }
                }
            });
        }
    };

    updateDependencies(updatedKpi);

    const updateValues = (metrics: Metric[], getKey: (id: number) => string): Metric[] =>
        metrics.map((metric) => {
            const key = getKey(metric.id);
            if (key in allMetricKpiValues) {
                let value = allMetricKpiValues[key];
                if (isNaN(value)) {
                    value = '0';
                } else if (value === Infinity) {
                    value = Number.MAX_SAFE_INTEGER.toString(); // 9007199254740991
                } else if (value === -Infinity) {
                    value = (-Number.MAX_SAFE_INTEGER).toString(); // -9007199254740991
                }
                return {
                    ...metric,
                    value: value,
                };
            }
            return metric;
        });

    return {
        ...ppg,
        metrics: updateValues(ppg.metrics, (id) => promoConfigDictionary.promoMetrics[id].name_key),
        spendDetails: updateValues(ppg.spendDetails, (id) => promoConfigDictionary.spendMetrics[id].name_key),
    };
};

export function getPromoMetricByNameKeyName(
    promoConfig: PromoConfigDictionary,
    targetNameKey: tpoPromoMetricKeys,
): ItemWithEditable {
    const { promoMetrics, spendMetrics } = promoConfig;

    const promoKey = Object.keys(promoMetrics).find((key) => promoMetrics[Number(key)]?.name_key === targetNameKey);

    if (promoKey !== undefined) {
        return promoMetrics[Number(promoKey)];
    }

    const spendKey = Object.keys(spendMetrics).find((key) => spendMetrics[Number(key)]?.name_key === targetNameKey);

    return spendMetrics[Number(spendKey)];
}

export function getQuarter(unixDate: number): string {
    const month = dayjs.unix(unixDate).utc().month(); // 0-indexed: Jan = 0
    if (month <= 2) return 'Q1';
    if (month <= 5) return 'Q2';
    if (month <= 8) return 'Q3';
    return 'Q4';
}

export function isPromoYearChangeNotAllowed(
    originalPromoStartDate: number | null,
    currentPromoStartDate: number | null,
    status: string,
): boolean {
    if (!originalPromoStartDate || !currentPromoStartDate) return false;

    const originalYear = dayjs.unix(originalPromoStartDate).year();
    const currentYear = dayjs.unix(currentPromoStartDate).year();

    return status?.toLowerCase() === 'approved' && originalYear !== currentYear;
}

export const isValidMetricInput = (value: string, metric: ItemWithEditable, setWarningMessage: Function): boolean => {
    if (!value) return false;
    value = String(value).trim();

    // Check if it's a valid number
    const num = Number(value);
    if (isNaN(num)) return false;

    // Check negative
    if (!metric.allowNegative && num < 0) {
        setWarningMessage('Negative values not allowed.');
        return false;
    }

    // Check min value (only if minValue is not null)
    if (metric.minValue !== null && num < metric.minValue) {
        setWarningMessage(`Minimum allowed value is ${metric.minValue}.`);
        return false;
    }

    // Check max value (only if maxValue is not null)
    if (metric.maxValue !== null && num > metric.maxValue) {
        setWarningMessage(`Maximum allowed value is ${metric.maxValue}.`);
        return false;
    }

    // Check decimal precision
    const hasDecimal = value.includes('.');
    const decimalPart = value.split('.')[1] || '';

    if (metric.allowedDecimals === 0 && hasDecimal) {
        setWarningMessage('Decimal values are not allowed.');
        return false;
    }

    if (decimalPart.length > metric.allowedDecimals) {
        setWarningMessage(`Maximum ${metric.allowedDecimals} decimal place(s) allowed.`);
        return false;
    }
    setWarningMessage('');
    return true;
};

/**
 * Restrict input to numbers, optional dot and minus.
 * Prevents multiple dots or misplaced minus signs.
 */
export const handleNumberInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentValue: string) => {
    const isNumber = e.key >= '0' && e.key <= '9';
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', '.', '-'];
    const isCtrlA = (e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey);

    if (!isNumber && !allowedKeys.includes(e.key) && !isCtrlA) {
        e.preventDefault();
    }

    // Prevent multiple dots
    if (e.key === '.' && currentValue.includes('.')) {
        e.preventDefault();
    }

    // Prevent multiple or misplaced minus signs
    if (e.key === '-' && (currentValue.includes('-') || currentValue.length > 0)) {
        e.preventDefault();
    }
};

export const handleRecommendSuccessDefaultValue = (
    updatedPPgs: ProductPPG[],
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const newUpdatedPPGs = _.cloneDeep(updatedPPgs);

    newUpdatedPPGs.map((ppg: ProductPPG) => {
        const allMetricKpiValues: any = {};

        // Extract current KPI values from `ppgs`
        ppg.metrics.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.promoMetrics[metric.id].name_key] = metric.value;
        });
        ppg.spendDetails.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.spendMetrics[metric.id].name_key] = metric.value;
        });

        // List of metric keys to process
        const metricKeys = [
            tpoPromoMetricKeys.SCAN_REDEMPTION_PERCENT,
            tpoPromoMetricKeys.TOTAL_PROMO_SPENDS,
            tpoPromoMetricKeys.NET_PRICE_INCL_MDF_PER_UNIT,
            tpoPromoMetricKeys.NET_PRICE_PER_STAT,
            tpoPromoMetricKeys.GROSS_MARGIN_DOLLARS,
            tpoPromoMetricKeys.CONSUMPTION_MPIC_STAT,
            tpoPromoMetricKeys.PROMOTED_AUR,
            tpoPromoMetricKeys.SCAN_VOLUME,
        ];
        // Loop through each metric key and process it
        metricKeys.forEach((metricKey) => {
            const formula = promoConfigDictionary.kpiFormulas[metricKey];
            let evaluatedValue: any = 0;
            if (formula) {
                try {
                    const expression = formula.replace(/\b[A-Za-z_]+\b/g, (match: any) => {
                        const value = allMetricKpiValues[match];
                        return value !== undefined && !isNaN(Number(value)) ? value.toString() : '0';
                    });

                    evaluatedValue = parser.evaluate(expression);

                    // Check for NaN or Infinity
                    if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
                        evaluatedValue = 0;
                    }

                    // Update metrics and spend details with the new calculated value
                    ppg.metrics.forEach((m: Metric) => {
                        const promoMetric = promoConfigDictionary.promoMetrics[m.id];
                        if (m.id === promoMetric.id && promoMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                        }
                    });
                    ppg.spendDetails.forEach((m: Metric) => {
                        const spendMetric = promoConfigDictionary.spendMetrics[m.id];
                        if (m.id === spendMetric.id && spendMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                        }
                    });
                } catch (error) {
                    console.error(`Error evaluating formula for ${metricKey}`, error);
                }
            }
        });
    });

    return newUpdatedPPGs;
};

export const validatePromoPastDates = (selectedEventInfo: PromoEvent | null) => {
    const promoStartDate = selectedEventInfo?.currentPromoTactic.promoStartDate ?? Infinity;
    const utcConvertedDate = dayjs.unix(promoStartDate).utc().format('MM/DD/YYYY'); // Ensure UTC interpretation
    const utcDaysObj = dayjs(utcConvertedDate, 'MM/DD/YYYY');
    const currentDate = dayjs().format('MM/DD/YYYY');
    const currentDateDayjsObj = dayjs(currentDate, 'MM/DD/YYYY');
    const isPastPromo = utcDaysObj.isBefore(currentDateDayjsObj);
    if (isPastPromo) {
        sendNotification('error', TpoMessages.error.INVALID_PROMO_DATE);
        return true;
    }
    return false;
};

export const sortPPGsToExitingOrder = (existingPPGs: ProductPPG[], newPPGs: ProductPPG[]) => {
    const existingPPGsOrder = existingPPGs?.map((p: ProductPPG) => p.id) || [];

    let updatedResponse = newPPGs.sort((a, b) => {
        const indexA = existingPPGsOrder.indexOf(a.id);
        const indexB = existingPPGsOrder.indexOf(b.id);

        if (indexA === -1 && indexB === -1) return 0; // Both are new/unknown, keep original order
        if (indexA === -1) return 1; // Move `a` to the end if `a.id` is new
        if (indexB === -1) return -1; // Move `b` to the end if `b.id` is new

        return indexA - indexB;
    });

    return updatedResponse;
};

export function validateEdlpQuarterSpan(
    selectedEventInfo: PromoEvent | null,
    promoConfigDictionary: PromoConfigDictionary,
): boolean {
    const warnings: string[] = [];
    const currentPromo = selectedEventInfo?.currentPromoTactic;
    if (currentPromo?.promoType !== PromotionTypes.EDLP) return false;

    const miscFixedMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.FIXED_MISC);
    const scanRateMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.SCAN_RATE);

    const promoStart = currentPromo?.promoStartDate;
    const promoEnd = currentPromo?.promoEndDate;

    if (!promoStart || !promoEnd) return true; // Skip if dates are not selected

    const startQuarter = getQuarter(promoStart);
    const endQuarter = getQuarter(promoEnd);

    currentPromo?.ppgs?.forEach((ppg) => {
        const miscFixed = Number(ppg.spendDetails.find((m) => m.id === miscFixedMetric.id)?.value ?? 0);
        const scanRate = Number(ppg.spendDetails.find((m) => m.id === scanRateMetric.id)?.value ?? 0);

        if ((miscFixed > 0 || scanRate > 0) && startQuarter !== endQuarter) {
            warnings.push(ppg.name ?? 'Unnamed PPG');
        }
    });

    if (warnings.length > 0) {
        sendNotification(
            'error',
            TpoMessages.error.EDLP_PPG_QUARTER_SPAN(miscFixedMetric.name, scanRateMetric.name, warnings),
        );
        return true;
    }

    return false;
}

export const resetEditableSpendsToZero = (ppgs: ProductPPG[], promoConfigDictionary: PromoConfigDictionary) => {
    ppgs.forEach((ppg: ProductPPG) => {
        ppg.spendDetails.forEach((spend: Metric) => {
            if (promoConfigDictionary.spendMetrics[spend.id]?.editable) {
                spend.value = '0';
            }
        });
    });
};

export const handleUnselectSpendMetrics = (
    selectedEventInfo: PromoEvent,
    removedMetricIds: number[],
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const newUpdatedPPGs = selectedEventInfo.currentPromoTactic.ppgs;
    // store all the editable removed ids
    const removedEditableMetrics: number[] = [];
    // set value as 0 for all the removed editable ids
    newUpdatedPPGs[0]?.spendDetails.forEach((m: Metric) => {
        if (removedMetricIds.includes(m.id) && promoConfigDictionary.spendMetrics[m.id].editable) {
            removedEditableMetrics.push(m.id);
        }
    });

    const newSelectedEventInfo = _.cloneDeep(selectedEventInfo);

    // Start with the updatedPPgs
    newSelectedEventInfo.currentPromoTactic.ppgs = _.cloneDeep(newUpdatedPPGs);

    const cascadedUpdatedPPGs = newSelectedEventInfo.currentPromoTactic.ppgs.map((ppg) => {
        let updatedPPG = ppg;

        removedEditableMetrics.forEach((mId) => {
            const updatedPPGList = handleMetricInputChange(
                promoConfigDictionary,
                newSelectedEventInfo,
                mId,
                ppg.name,
                '0',
                false, // it's a metric trigger
            );

            if (Array.isArray(updatedPPGList)) {
                const foundPPG = updatedPPGList.find((p) => p.name === ppg.name);
                if (foundPPG) {
                    updatedPPG = foundPPG;
                }
            }
        });

        return updatedPPG;
    });

    newSelectedEventInfo.currentPromoTactic.ppgs = cascadedUpdatedPPGs;

    return newSelectedEventInfo;
};
